import type { z } from 'zod/v3';

import { ModelError } from './error';

/**
 * DTO를 Model로 안전하게 파싱
 *
 * @param schema - Zod 스키마
 * @param data - 파싱할 데이터 (API 응답)
 * @param modelName - 에러 메시지에 사용할 모델 이름
 * @returns 파싱된 데이터
 * @throws {ModelError} 파싱 실패 시
 *
 * @example
 * ```ts
 * const partner = parseModel(partnerSchema, apiResponse, 'Partner');
 * ```
 */
export function parseModel<T>(
  schema: z.ZodType<T>,
  data: unknown,
  modelName: string,
): T {
  const result = schema.safeParse(data);

  if (!result.success) {
    throw new ModelError(
      `${modelName} 파싱 실패`,
      result.error.issues as {
        code: string;
        path: (string | number)[];
        message: string;
      }[],
      result.error,
    );
  }

  return result.data;
}

/**
 * DTO 배열을 Model 배열로 안전하게 파싱
 *
 * @param schema - 개별 아이템의 Zod 스키마
 * @param data - 파싱할 배열 데이터
 * @param modelName - 에러 메시지에 사용할 모델 이름
 * @returns 파싱된 데이터 배열
 * @throws {ModelError} 파싱 실패 시
 */
export function parseModelArray<T>(
  schema: z.ZodType<T>,
  data: unknown[],
  modelName: string,
): T[] {
  return data.map((item, index) => {
    const result = schema.safeParse(item);

    if (!result.success) {
      throw new ModelError(
        `${modelName}[${index}] 파싱 실패`,
        result.error.issues as {
          code: string;
          path: (string | number)[];
          message: string;
        }[],
        result.error,
      );
    }

    return result.data;
  });
}

/**
 * DTO를 Model로 안전하게 파싱 (실패 시 null 반환)
 *
 * @param schema - Zod 스키마
 * @param data - 파싱할 데이터
 * @returns 파싱된 데이터 또는 null
 */
export function safeParseModel<T>(
  schema: z.ZodType<T>,
  data: unknown,
): T | null {
  const result = schema.safeParse(data);
  return result.success ? result.data : null;
}
